import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./TOIUpdatedLandingPage.module.css";
import "../../../assets/css/toi/common.css";
import {
  toiLandingContinueClick,
  toiLandingPageLanded,
} from "../../../libs/events";
import toiLogoDark from "../../../assets/img/toi/updatedLanding/toi-logo-dark.svg";
import toiLogoLight from "../../../assets/img/toi/updatedLanding/toi-logo-light.svg";
import aboundLogoDark from "../../../assets/img/toi/updatedLanding/abound-logo-dark.svg";
import aboundLogoLight from "../../../assets/img/toi/updatedLanding/abound-logo-light.svg";
import bonusGiftIcon from "../../../assets/img/toi/updatedLanding/gift-small-white.svg";
import familyImage from "../../../assets/img/toi/updatedLanding/toi-tp-family-1.jpeg";
import USFlag from "../../../assets/img/toi/updatedLanding/us-flag.svg";
import IndiaFlag from "../../../assets/img/toi/updatedLanding/india-flag.svg";
import {
  // APPLIED_UPDATED_LANDING_FAQ_VARIANT,
  // TOI_UPDATED_LANDING_FAQ_VARIANT,
  TOI_WELCOME_BONUS,
} from "../../../constants";

const FAQS = [
  {
    question: "What is Abound?",
    answer:
      "Abound is a financial app built for Indian NRIs in the US. We offer the best FX rates to India, cashback on transfers, and tools to help you manage your money across borders — all in one place.",
  },
  {
    question: `Is the ${TOI_WELCOME_BONUS} really mine?`,
    answer:
      "Yes — it's a welcome bonus credited to your Abound wallet with absolutely no restrictions. Use it to send money to India, no minimum required.",
  },
  {
    question: "How long does a transfer take?",
    answer:
      "Your transaction to India will mostly be settled on the same day. You'll get a notification the moment your money lands.",
  },
  {
    question: "Can I withdraw it instead of sending it?",
    answer:
      "No, You can’t withdraw directly. The $50 welcome bonus is applied directly to your first transfer to India. It reduces the amount you need to send out of pocket — so you send less, your family receives more.",
  },
  {
    question: "Are there any fees or minimums?",
    answer:
      "No hidden fees and in fact you will get a free first transfer. Abound shows you the exact amount your recipient gets before you confirm. There's no minimum transfer amount to get started.",
  },
];

const RATE_PREVIEW = "$1 = ₹105";
const SEND_AMOUNT = "$50";
const RECIPIENT_AMOUNT = "₹5,250";

function TOIUpdatedLandingPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => {
    const stored = window.sessionStorage.getItem("theme") || "";
    return stored || "light";
  });
  const landingEventFired = useRef(false);
  const viewAppearTsRef = useRef(null);
  const hasUserInteracted = useRef(false);
  const faqItemRefs = useRef([]);
  const shouldScrollOpenedFaq = useRef(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const isLight = (theme || "").toLowerCase() === "light";
  // const showFaq =
  //   APPLIED_UPDATED_LANDING_FAQ_VARIANT ===
  //   TOI_UPDATED_LANDING_FAQ_VARIANT.ENABLED;

  const showFaq = true;

  useEffect(() => {
    const stored = window.sessionStorage.getItem("theme") || "light";
    setTheme(stored);
  }, []);

  useEffect(() => {
    if (!showFaq || openFaqIndex === null || !shouldScrollOpenedFaq.current) {
      return undefined;
    }

    const scrollTimer = setTimeout(() => {
      const openedFaq = faqItemRefs.current[openFaqIndex];
      if (!openedFaq || typeof window === "undefined") return;

      const footerSafeArea = 120;
      const rect = openedFaq.getBoundingClientRect();
      const visibleBottom = window.innerHeight - footerSafeArea;
      const isFullyVisible = rect.top >= 0 && rect.bottom <= visibleBottom;

      if (!isFullyVisible) {
        openedFaq.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      shouldScrollOpenedFaq.current = false;
    }, 80);

    return () => clearTimeout(scrollTimer);
  }, [openFaqIndex, showFaq]);

  useEffect(() => {
    const isIosWebView =
      typeof window !== "undefined" &&
      typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined";

    const fireLandingEvent = (source, options = {}) => {
      const force = options?.force === true;
      if (document.visibilityState !== "visible") return;
      if (!force) {
        if (landingEventFired.current) return;
        landingEventFired.current = true;
      }
      toiLandingPageLanded({
        utm_source: "toi_mobile",
        utm_medium: "toi_mobile",
        utm_campaign: "toi_mobile",
        trigger_source: source,
        view_appear_ts: viewAppearTsRef.current || undefined,
        landing_page_type: "landing_page_dollar_fifty",
        faq_variant_enabled: showFaq,
      });
    };

    const onMessage = (evt) => {
      try {
        let payload = evt?.data;
        if (!payload) return;
        if (typeof payload === "string") payload = JSON.parse(payload);
        if (payload?.eventType === "viewAppear") {
          viewAppearTsRef.current = payload?.timestamp || Date.now();
          fireLandingEvent("ios_viewAppear", { force: true });
        }
      } catch (e) {}
    };

    const onInteraction = () => {
      if (hasUserInteracted.current) return;
      hasUserInteracted.current = true;
      fireLandingEvent("user_interaction");
    };

    const interactionEvents = ["click", "touchstart", "keydown"];
    interactionEvents.forEach((event) => {
      window.addEventListener(event, onInteraction, { once: true });
    });

    if (isIosWebView) {
      window.addEventListener("message", onMessage);
      return () => {
        interactionEvents.forEach((event) =>
          window.removeEventListener(event, onInteraction),
        );
        window.removeEventListener("message", onMessage);
      };
    }

    fireLandingEvent("land");
    return () => {
      interactionEvents.forEach((event) =>
        window.removeEventListener(event, onInteraction),
      );
    };
  }, [showFaq]);

  const handleContinue = () => {
    toiLandingContinueClick({
      click_source: "landing_page_updated",
      faq_variant_enabled: showFaq,
    });
    // setTimeout(() => navigate("/onboarding/recipient-bank-details"), 50);
    setTimeout(() => navigate("/onboarding/signup"), 50);
  };

  const handleFaqToggle = (index) => {
    shouldScrollOpenedFaq.current = true;
    setOpenFaqIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  return (
    <div
      className={styles.container}
      data-theme={isLight ? "light" : "dark"}
      data-faq-enabled={showFaq ? "true" : "false"}
    >
      <section className={styles.heroSection}>
        <header className={styles.brandBar}>
          <div
            className={styles.brandLockup}
            aria-label="The Times of India x Abound"
          >
            <img
              src={isLight ? toiLogoLight : toiLogoDark}
              alt="The Times of India"
              className={styles.toiLogo}
            />
            <span className={styles.brandCross}>x</span>
            <img
              src={isLight ? aboundLogoLight : aboundLogoDark}
              alt="Abound"
              className={styles.aboundLogo}
            />
          </div>
        </header>

        <main className={styles.mainContent}>
          <div className={styles.rateBlock}>
            <h1 className={styles.headline}>
              Claim {TOI_WELCOME_BONUS} bonus &amp; send to India at
            </h1>
            <div className={styles.ratePill}>{RATE_PREVIEW}</div>
            <p className={styles.subhead}>
              Guaranteed best FX rate. Fast settlement.
            </p>
          </div>

          <div className={styles.transferCard}>
            <div className={styles.currencyCol}>
              <span className={styles.currencyLabel}>You send</span>
              <strong>
                <img src={USFlag} alt="" aria-hidden />
                {SEND_AMOUNT}
              </strong>
            </div>

            <span className={styles.exchangeArrow} aria-hidden>
              →
            </span>

            <div className={`${styles.currencyCol} ${styles.currencyRight}`}>
              <span className={styles.currencyLabel}>Recipient gets</span>
              <strong>
                <img src={IndiaFlag} alt="" aria-hidden />
                {RECIPIENT_AMOUNT}
              </strong>
            </div>
          </div>
        </main>
      </section>

      <section className={styles.imageSection}>
        <img src={familyImage} alt="" className={styles.familyImage} />
        <div className={styles.bonusPill}>
          <img src={bonusGiftIcon} alt="" aria-hidden />
          <p>
            {TOI_WELCOME_BONUS} welcome bonus for TOI readers
          </p>
        </div>
      </section>

      {showFaq ? (
        <section className={styles.faqSection}>
          <h2>Frequently Asked Questions</h2>
          <div className={styles.faqList}>
            {FAQS.map((faq, index) => (
              <details
                key={faq.question}
                ref={(node) => {
                  faqItemRefs.current[index] = node;
                }}
                className={styles.faqItem}
                open={openFaqIndex === index}
              >
                <summary
                  onClick={(evt) => {
                    evt.preventDefault();
                    handleFaqToggle(index);
                  }}
                >
                  <span>{faq.question}</span>
                  <span className={styles.chevron} aria-hidden />
                </summary>
                {faq.answer ? <p>{faq.answer}</p> : null}
              </details>
            ))}
          </div>
        </section>
      ) : null}

      <div className={styles.footer}>
        <button className={styles.ctaBtn} onClick={handleContinue}>
          <span>Claim {TOI_WELCOME_BONUS} Welcome Bonus</span>
          <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}

export default TOIUpdatedLandingPage;
